import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
    Box,
    FormControlLabel,
    Checkbox,
    Typography,
    CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import { contactClient } from "../redux/ducks/client";
import { showAlert } from "../redux/slices/alerts";

const HelperText = styled(DialogContentText)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const RecipientSection = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    marginBottom: theme.spacing(2),
}));

const RecipientLabel = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    marginBottom: theme.spacing(0.5),
}));

const RecipientEmail = styled("span")(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const CopyNote = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
}));

const EmptyState = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const SubjectField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const ContactClientDialog = ({ open, client, onClose, defaultSubject = "" }) => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth.currentUser);

    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [includePrimaryContact, setIncludePrimaryContact] = useState(true);
    const [includeBillingContact, setIncludeBillingContact] = useState(true);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSubject(defaultSubject);
        setMessage("");
        setIncludePrimaryContact(true);
        setIncludeBillingContact(true);
        setSending(false);
    }, [open, client?._id, defaultSubject]);

    const primaryEmail = client?.primaryContact?.email;
    const billingEmail = client?.billingContact;
    const hasAnyContact = Boolean(primaryEmail || billingEmail);
    const selectedRecipient = (primaryEmail && includePrimaryContact) || (billingEmail && includeBillingContact);
    const canSend = hasAnyContact && Boolean(selectedRecipient) && subject.trim() !== "" && message.trim() !== "" && !sending;

    const handleSend = () => {
        if (!canSend) return;
        setSending(true);
        dispatch(
            contactClient(
                client._id,
                {
                    subject: subject.trim(),
                    message: message.trim(),
                    includePrimaryContact: Boolean(primaryEmail) && includePrimaryContact,
                    includeBillingContact: Boolean(billingEmail) && includeBillingContact,
                },
                (res) => {
                    if (!res) {
                        setSending(false);
                        return;
                    }
                    dispatch(showAlert({ severity: "success", message: "Email sent." }));
                    setSubject("");
                    setMessage("");
                    setSending(false);
                    onClose();
                },
            ),
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Contact {client?.name}</DialogTitle>
            <DialogContent>
                <HelperText>
                    This sends an email from the Creative Works system address. Your name is on it and replies come straight to your inbox, so you can continue the conversation by
                    email.
                </HelperText>
                {hasAnyContact ? (
                    <RecipientSection>
                        <RecipientLabel variant="subtitle2">Send to</RecipientLabel>
                        {primaryEmail && (
                            <FormControlLabel
                                control={<Checkbox checked={includePrimaryContact} onChange={(e) => setIncludePrimaryContact(e.target.checked)} />}
                                label={
                                    <span>
                                        Primary contact <RecipientEmail>({primaryEmail})</RecipientEmail>
                                    </span>
                                }
                            />
                        )}
                        {billingEmail && (
                            <FormControlLabel
                                control={<Checkbox checked={includeBillingContact} onChange={(e) => setIncludeBillingContact(e.target.checked)} />}
                                label={
                                    <span>
                                        Billing contact <RecipientEmail>({billingEmail})</RecipientEmail>
                                    </span>
                                }
                            />
                        )}
                        <CopyNote variant="caption">A copy goes to you ({currentUser?.email}); replies come to you.</CopyNote>
                    </RecipientSection>
                ) : (
                    <EmptyState variant="body2">No primary or billing contact email is on file for this client.</EmptyState>
                )}
                <SubjectField
                    label="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    fullWidth
                    required
                    disabled={!hasAnyContact || sending}
                    inputProps={{ maxLength: 200 }}
                />
                <TextField
                    label="Message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    fullWidth
                    multiline
                    rows={6}
                    required
                    disabled={!hasAnyContact || sending}
                    inputProps={{ maxLength: 5000 }}
                    helperText={`${message.length}/5000`}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={sending}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSend} disabled={!canSend}>
                    {sending ? <CircularProgress size={20} /> : "Send Email"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContactClientDialog;
